import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { LuArrowLeft, LuArrowRight, LuX } from "react-icons/lu";
import { useMenu } from "../../context/MenuContext";
import { UseMediaQuery } from "../../hooks/UseMediaQuery";
import { isNewAccount, markSeen, OPEN_TOUR_EVENT, setTourOpen, TOUR_STUDENT, TOUR_TEACHER, wasSeen } from "../../helpers/tour/tour";
import { placeCard, useTargetRect } from "./useTargetRect";
import * as styled from "./CabinetTour.styled";

// Тур-підказки по кабінету: підсвічує пункт меню чи блок і пояснює, навіщо він. Сам відкривається новому
// акаунту один раз (що бачив — у базі), повторно — кнопкою «Підказки по кабінету» на головній.
// target — селектор data-tour; немає елемента на сторінці — картка просто по центру.
// optional — крок лише коли блок є (бот уже підключено — блоку немає, крок пропускаємо).
// points — головне з правил школи: на них окремий акцент, тому крок одразу другий.
const STEPS = {
  student: [
    { title: "Привіт! Це твій кабінет Speaksy", text: "За пів хвилини покажемо, де що лежить. Можна пропустити й повернутись до туру пізніше з головної." },
    {
      target: "rules",
      title: "Правила школи — важливо",
      text: "Головне, що варто знати з першого дня:",
      points: [
        "Скасувати чи перенести урок — щонайменше за 4 години, у «Розкладі». Пізніше — урок вважається пройденим.",
        "Запізнення не компенсується: урок закінчується вчасно.",
        "Гроші за залишок уроків не повертаються, але можна взяти паузу й повернутись будь-коли — уроки збережуться.",
      ],
      footnote: "Усі правила — тут, у «Правилах школи».",
    },
    { target: "telegram-card", optional: true, title: "Підключи Telegram-бот", text: "Нагадування про уроки й баланс, зірки й магазин — прямо в Telegram. Натисни «Підключити Telegram»." },
    { target: "nav-schedule", nav: true, title: "Розклад", text: "Дні й час твоїх уроків. Тут же можна скасувати або перенести урок." },
    { target: "nav-homework", nav: true, title: "Домашнє завдання", text: "Після кожного уроку тут зʼявляється ДЗ від вчителя. Натисни «Виконати ДЗ» — за кожне здане +1 ⭐, зірки міняй у «Магазині»." },
    { target: "nav-payment", nav: true, title: "Оплата", text: "Скільки уроків лишилось на балансі й як поповнити пакет." },
    { target: "first-steps", title: "Готово!", text: "Ось твої перші кроки — виконуй по черзі. Тур завжди можна повторити внизу головної.",
      // Чекліст уже прихований або все виконано
      fallback: "Тепер ти знаєш, де що. Тур завжди можна повторити внизу головної." },
  ],
  teacher: [
    { title: "Вітаємо в кабінеті вчителя Speaksy", text: "Покажемо головне за пів хвилини. Тур можна повторити будь-коли з головної." },
    {
      target: "rules",
      title: "Правила школи — важливо",
      text: "Головне, від чого залежить оплата:",
      points: [
        "Учень не прийшов за 5 хв — напишіть менеджеру й не виходьте з уроку до відповіді. Не повідомили — урок не оплачується.",
        "Неявка без попередження — штраф 150 грн, скасування в день уроку (після 21:00 напередодні) — 75 грн.",
      ],
      footnote: "Усі правила — тут, у «Пригадати правила школи».",
    },
    { target: "telegram-card", optional: true, title: "Підключіть бот вчителів", text: "Запити на пробні уроки приходитимуть у Telegram — так ви першими отримуєте нових учнів." },
    { target: "mark-lessons", title: "Відмітка після кожного уроку", text: "Тут уроки за сьогодні й учора. Провели урок — «Зробити відмітку». Відмітити можна протягом 24 годин, без відмітки урок не зарахується в оплату." },
    { target: "nav-schedule", nav: true, title: "Графік", text: "Ваш розклад з учнями на тиждень: усі уроки, переноси й скасування." },
    { target: "nav-marked-lessons", nav: true, title: "Пройдені уроки", text: "Історія відміток. Тут же перевіряєте ДЗ учнів — кнопка «Перевірити ДЗ»." },
    { target: "nav-finance", nav: true, title: "Фінанси", text: "Скільки уроків проведено і яка зарплата за період. Матеріали для занять — у «Матеріалах»." },
  ],
};

// На телефоні картка на всю ширину: під підсвіченим, над ним, а якщо ніде не влазить — унизу екрана
const dockCard = (spot, height) => {
  const vh = window.innerHeight;
  const base = { left: 16, width: window.innerWidth - 32 };
  if (!spot) return { ...base, top: vh - height - 16 };
  if (spot.top + spot.height + 12 + height + 16 <= vh) return { ...base, top: spot.top + spot.height + 12 };
  if (spot.top - 12 - height >= 16) return { ...base, top: spot.top - 12 - height };
  return { ...base, top: vh - height - 16 };
};

const present = (target) => Boolean(document.querySelector(`[data-tour="${target}"]`));

const TOUR_ID = { student: TOUR_STUDENT, teacher: TOUR_TEACHER };

function CabinetTour({ role, user, homePath, onActiveChange }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { setShowMenu } = useMenu();
  const { isMobile } = UseMediaQuery();
  const tourId = TOUR_ID[role];
  const [open, setOpen] = useState(false);
  const autoShown = useRef(false);
  const [step, setStep] = useState(0);
  const [cardHeight, setCardHeight] = useState(230);
  const cardRef = useRef(null);
  // Необовʼязкові кроки — за тим, що є на головній, коли тур стартував (блоки довантажуються — даємо секунду)
  const [optionalShown, setOptionalShown] = useState([]);
  const steps = STEPS[role].filter((item) => !item.optional || optionalShown.includes(item.target));

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => {
      setOptionalShown(STEPS[role].filter((item) => item.optional && present(item.target)).map((item) => item.target));
    }, 1000);
    return () => clearTimeout(timer);
  }, [open, role]);

  const current = steps[step];
  // Меню на телефоні — шторка: для пунктів меню відкриваємо її, для блоків сторінки — ховаємо
  const menuStep = isMobile && current?.nav;
  const rect = useTargetRect(current?.target && `[data-tour="${current.target}"]`, open);

  useEffect(() => {
    setTourOpen(open);
    onActiveChange?.(open);
  }, [open, onActiveChange]);
  useEffect(() => () => setTourOpen(false), []);

  // Сам — лише коли людина на головній: якщо прийшла за посиланням одразу на ДЗ чи оплату, не перекидаємо
  useEffect(() => {
    if (autoShown.current || pathname !== homePath || !isNewAccount(user) || wasSeen(user, tourId)) return;
    autoShown.current = true;
    setOpen(true);
  }, [pathname, homePath, user, tourId]);

  useEffect(() => {
    const reopen = () => {
      setStep(0);
      setOpen(true);
      navigate(homePath);
    };
    window.addEventListener(OPEN_TOUR_EVENT, reopen);
    return () => window.removeEventListener(OPEN_TOUR_EVENT, reopen);
  }, [navigate, homePath]);

  useEffect(() => {
    if (open && isMobile) setShowMenu(Boolean(menuStep));
  }, [open, isMobile, menuStep, setShowMenu]);

  // Блок нижче екрана — прокручуємо до нього. На телефоні — до верху екрана: картка сідає знизу і його не закриває
  useEffect(() => {
    if (!open || !current?.target || current.nav) return;
    const element = document.querySelector(`[data-tour="${current.target}"]`);
    if (!element) return;
    element.style.scrollMarginTop = "16px";
    element.scrollIntoView({ block: isMobile ? "start" : "center", behavior: isMobile ? "auto" : "smooth" });
  }, [open, current, isMobile]);

  // Висота картки різна (текст), а від неї залежить, куди її ставити
  useLayoutEffect(() => {
    if (cardRef.current) setCardHeight(cardRef.current.offsetHeight);
  }, [open, step, rect, isMobile]);

  const finish = useCallback(() => {
    setOpen(false);
    if (isMobile) setShowMenu(false);
    markSeen(dispatch, tourId);
    // Вікно «Тепер у Speaksy є зірки» після туру не потрібне — про зірки вже розповіли
    if (role === "student") {
      try {
        window.localStorage.setItem("starsIntroSeen", "1");
      } catch (error) {}
    }
  }, [dispatch, tourId, role, isMobile, setShowMenu]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event) => {
      if (event.key === "Escape") finish();
      if (event.key === "ArrowRight") setStep((value) => Math.min(value + 1, steps.length - 1));
      if (event.key === "ArrowLeft") setStep((value) => Math.max(value - 1, 0));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, finish, steps.length]);

  if (!open || !current) return null;

  const last = step === steps.length - 1;
  const spot = rect && { top: rect.top - 6, left: rect.left - 6, width: rect.width + 12, height: rect.height + 12 };
  const place = isMobile ? dockCard(spot, cardHeight) : placeCard(spot, { width: 340, height: cardHeight });

  return createPortal(
    <styled.Layer>
      {spot ? <styled.Spot style={spot} /> : <styled.Dim />}
      <styled.Card
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-label={current.title}
        style={{ top: place.top, left: place.left, width: place.width }}
        key={step}
      >
        <styled.Close type="button" aria-label="Закрити підказки" onClick={finish}>
          <LuX size={18} />
        </styled.Close>
        <styled.Counter>
          {step + 1} / {steps.length}
        </styled.Counter>
        <styled.Title>{current.title}</styled.Title>
        <styled.Text>{!rect && current.fallback ? current.fallback : current.text}</styled.Text>
        {current.points && (
          <styled.Points>
            {current.points.map((point) => (
              <li key={point}>{point}</li>
            ))}
          </styled.Points>
        )}
        {current.footnote && <styled.Footnote>{current.footnote}</styled.Footnote>}
        <styled.Footer>
          {last ? (
            <span />
          ) : (
            <styled.Skip type="button" onClick={finish}>
              Пропустити
            </styled.Skip>
          )}
          <styled.Buttons>
            {step > 0 && (
              <styled.Back type="button" aria-label="Назад" onClick={() => setStep(step - 1)}>
                <LuArrowLeft size={18} />
              </styled.Back>
            )}
            <styled.Next type="button" onClick={() => (last ? finish() : setStep(step + 1))}>
              {last ? "Почати" : "Далі"} {!last && <LuArrowRight size={18} />}
            </styled.Next>
          </styled.Buttons>
        </styled.Footer>
      </styled.Card>
    </styled.Layer>,
    document.body
  );
}

export default CabinetTour;
